import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { useTranslation } from 'react-i18next';
import { ALL_STORE_PAYMENT_METHODS, getStorePaymentMethodKey } from '../../constants/StorePaymentMethod';
import { BRAND } from '../../theme/brandTokens';
import useBrandPalette from '../../theme/useBrandPalette';
import { formatCurrency } from '../../utils/currencyUtils';

const SERIES_ID = 'cash-flow-methods';
const HOVER_FINE_QUERY = '(hover: hover) and (pointer: fine)';

const readsAsTouchOnly = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return !window.matchMedia(HOVER_FINE_QUERY).matches;
};

const useTouchOnly = () => {
  const [touchOnly, setTouchOnly] = useState(readsAsTouchOnly);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(HOVER_FINE_QUERY);
    const update = () => setTouchOnly(!query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return touchOnly;
};

export const formatShareOfTotal = (amount, total) => {
  const safeTotal = Number(total) || 0;
  const safeAmount = Number(amount) || 0;
  if (safeTotal <= 0) return '0%';
  const percent = Math.round((safeAmount / safeTotal) * 1000) / 10;
  const text = Number.isInteger(percent) ? String(percent) : percent.toFixed(1).replace('.', ',');
  return `${text}%`;
};

const sliceColors = (brand) => ([
  brand.primary,
  brand.accent,
  brand.primaryDark,
  brand.accentDark,
  lighten(brand.primary, 0.28),
  lighten(brand.accent, 0.22),
]);

const CashFlowDonutCenter = ({ caption, total }) => {
  const { width, height, left, top } = useDrawingArea();
  const theme = useTheme();
  const x = left + width / 2;
  const y = top + height / 2;
  const fontFamily = theme.typography.fontFamily;

  return (
    <g>
      <text
        x={x}
        y={y - 8}
        textAnchor="middle"
        fill={BRAND.textMuted}
        fontSize={12}
        fontFamily={fontFamily}
      >
        {caption}
      </text>
      <text
        x={x}
        y={y + 16}
        textAnchor="middle"
        fill={BRAND.primaryText}
        fontSize={16}
        fontWeight={700}
        fontFamily={fontFamily}
      >
        {total}
      </text>
    </g>
  );
};

const PayrollCashFlowDonut = ({ report, currencyCode }) => {
  const { t } = useTranslation();
  const brand = useBrandPalette();
  const touchOnly = useTouchOnly();
  const [selectedId, setSelectedId] = useState(null);

  const total = Number(report?.totalReceived) || 0;
  const colors = useMemo(() => sliceColors(brand), [brand]);

  const slices = useMemo(() => {
    const receivedByMethod = new Map(
      (report?.byPaymentMethod || []).map((item) => [item.paymentMethod, Number(item.amountReceived) || 0]),
    );

    return ALL_STORE_PAYMENT_METHODS.flatMap((method, index) => {
      const amount = receivedByMethod.get(method) || 0;
      if (amount <= 0) return [];
      return [{
        id: method,
        value: amount,
        label: t(`storePaymentMethods.${getStorePaymentMethodKey(method)}`),
        color: colors[index % colors.length],
      }];
    });
  }, [colors, report?.byPaymentMethod, t]);

  const slicesKey = slices.map((slice) => `${slice.id}:${slice.value}`).join('|');

  useEffect(() => {
    setSelectedId(null);
  }, [slicesKey]);

  const selected = slices.find((slice) => slice.id === selectedId) || null;
  const hasSlices = slices.length > 0;
  const emptyColor = lighten(brand.text, 0.78);
  const chartData = hasSlices
    ? slices
    : [{ id: 'empty', value: 1, label: ' ', color: emptyColor }];

  const detailFor = (amount) => t('employeePayments.payroll.cashFlow.sliceDetail', {
    amount: formatCurrency(amount, currencyCode),
    percent: formatShareOfTotal(amount, total),
  });

  const selectSlice = (id) => {
    if (!touchOnly || !id || id === 'empty') return;
    setSelectedId((current) => (current === id ? null : id));
  };

  return (
    <Box data-testid="payroll-cash-flow-chart" sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
      <PieChart
        height={240}
        skipAnimation
        hideLegend={!hasSlices}
        series={[{
          id: SERIES_ID,
          data: chartData,
          innerRadius: '62%',
          outerRadius: '92%',
          paddingAngle: hasSlices && slices.length > 1 ? 1.5 : 0,
          cornerRadius: 4,
          valueFormatter: (item) => (hasSlices ? detailFor(item?.value) : null),
        }]}
        slotProps={{
          legend: {
            direction: 'horizontal',
            position: { vertical: 'bottom', horizontal: 'middle' },
            onItemClick: touchOnly
              ? (_event, legendItem) => selectSlice(legendItem.itemId)
              : undefined,
          },
          tooltip: {
            trigger: touchOnly || !hasSlices ? 'none' : 'item',
          },
        }}
        onItemClick={(_event, _identifier, item) => selectSlice(item?.id)}
        sx={{
          '& .MuiChartsLegend-root': {
            justifyContent: 'center',
          },
        }}
      >
        <CashFlowDonutCenter
          caption={t('employeePayments.payroll.cashFlow.totalReceived')}
          total={formatCurrency(total, currencyCode)}
        />
      </PieChart>

      {touchOnly && hasSlices && (
        <Box sx={{ mt: 1, minHeight: 48, textAlign: 'center' }} aria-live="polite">
          {selected ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
                {selected.label}
              </Typography>
              <Typography variant="body2" sx={{ color: BRAND.text }}>
                {detailFor(selected.value)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('employeePayments.payroll.cashFlow.tapSlice')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PayrollCashFlowDonut;
